// pkg139 — Firm-Wide Dates retheme catch-up: pre-pkg72 blue #2f5496 (table header, RANGE/FIRM badges ×2, links ×2) never made the sweep list; header+badges → NAVY_DEEP #0F1838, links → NAVY #1E2761, zebra #f8f9ff → #fafafa per platform convention — delivered 2026-07-31
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/adminApi';

function parseDate(dateStr) {
  const s = dateStr.substring(0, 10);
  const [yr, mo, dy] = s.split('-').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[mo-1]} ${dy}, ${yr}`;
}

export default function AdminGlobalDatesPage() {
  const [dates,   setDates]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [adding,  setAdding]  = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', description: '', location: '', isRange: false });

  useEffect(() => {
    adminApi.getGlobalDates()
      .then(data => { setDates(data); setLoading(false); })
      .catch(err  => { setError(err.message); setLoading(false); });
  }, []);

  function set(key, val) { setForm(p => ({ ...p, [key]: val })); }

  async function handleAdd(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const added = await adminApi.addGlobalDate({
        start_date: form.start_date,
        end_date:   form.isRange && form.end_date ? form.end_date : null,
        description: form.description,
        location:   form.location || null,
      });
      setDates(prev => [...prev, added].sort((a, b) => a.start_date.localeCompare(b.start_date)));
      setForm({ start_date: '', end_date: '', description: '', location: '', isRange: false });
      setAdding(false);
      setSuccess('Date added. It is now visible to all clients.');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    try { await adminApi.deleteGlobalDate(id); setDates(prev => prev.filter(d => d.id !== id)); }
    catch (err) { setError(err.message); }
  }

  const td = { padding: '10px 12px', fontSize: 13, fontFamily: 'Arial, sans-serif', borderBottom: '1px solid #eee', verticalAlign: 'top' };

  return (
    <div>
      <h1 className="page-title">Firm-Wide Dates</h1>
      <p className="page-subtitle">
        Dates added here are visible to <strong>all clients</strong> on their dashboard.
        Use these for firm holidays, vacation periods, court closures, or other notices.
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {adding ? (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title">Add Firm-Wide Date</div>
          <form onSubmit={handleAdd}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={!form.isRange} onChange={() => set('isRange', false)} /> Single Day
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={form.isRange} onChange={() => set('isRange', true)} /> Date Range
              </label>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{form.isRange ? 'Start Date' : 'Date'}</label>
                <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} required />
              </div>
              {form.isRange && (
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} min={form.start_date || undefined} required />
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Description</label>
              <input type="text" value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="e.g. Office Closed — Thanksgiving" required />
            </div>
            <div className="form-group">
              <label>Location or Link <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
              <input type="text" value={form.location} onChange={e => set('location', e.target.value)}
                placeholder="Physical address or URL" />
            </div>
            <div className="btn-row">
              <button type="button" className="btn" onClick={() => { setAdding(false); setError(''); }}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add to All Client Portals'}</button>
            </div>
          </form>
        </div>
      ) : (
        <button className="btn btn-primary" onClick={() => setAdding(true)} style={{ marginBottom: 20 }}>+ Add Firm-Wide Date</button>
      )}

      {loading ? (
        <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading...</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="desktop-only table-wrap">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
                <thead>
                  <tr style={{ background: '#0F1838' }}>
                    {['Date','Description','Location / Link',''].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dates.length === 0 && (
                    <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#888' }}>No firm-wide dates yet.</td></tr>
                  )}
                  {dates.map((d, i) => {
                    const isRange = !!d.end_date;
                    const isLink  = d.location && (d.location.startsWith('http://') || d.location.startsWith('https://'));
                    return (
                      <tr key={d.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...td, fontWeight: 500, width: 160, whiteSpace: 'nowrap' }}>
                          {parseDate(d.start_date)}
                          {isRange && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>through {parseDate(d.end_date)}</div>}
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: 10, background: '#0F1838', color: '#fff', borderRadius: 3, padding: '1px 6px', marginRight: 6 }}>{isRange ? 'RANGE' : 'FIRM'}</span>
                          {d.description}
                        </td>
                        <td style={{ ...td, color: '#555', fontSize: 12 }}>
                          {d.location ? isLink
                            ? <a href={d.location} target="_blank" rel="noopener noreferrer" style={{ color: '#1E2761' }}>🔗 {d.location}</a>
                            : <span>📍 {d.location}</span>
                          : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ ...td, textAlign: 'center', width: 80 }}>
                          <button onClick={() => handleDelete(d.id)}
                            style={{ background: 'none', border: '1px solid #B85042', color: '#B85042', borderRadius: 3, padding: '3px 10px', cursor: 'pointer', fontSize: 12, fontFamily: 'Arial, sans-serif' }}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="mobile-only">
            {dates.length === 0 && (
              <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>No firm-wide dates yet.</div>
            )}
            {dates.map(d => {
              const isRange = !!d.end_date;
              const isLink  = d.location && (d.location.startsWith('http://') || d.location.startsWith('https://'));
              return (
                <div key={d.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                        {parseDate(d.start_date)}
                        {isRange && <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}> – {parseDate(d.end_date)}</span>}
                      </div>
                      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, background: '#0F1838', color: '#fff', borderRadius: 3, padding: '1px 6px', marginRight: 6 }}>{isRange ? 'RANGE' : 'FIRM'}</span>
                        {d.description}
                      </div>
                      {d.location && (
                        <div style={{ fontSize: 12, color: '#555', fontFamily: 'Arial, sans-serif' }}>
                          {isLink
                            ? <a href={d.location} target="_blank" rel="noopener noreferrer" style={{ color: '#1E2761' }}>🔗 {d.location}</a>
                            : <span>📍 {d.location}</span>}
                        </div>
                      )}
                    </div>
                    <button onClick={() => handleDelete(d.id)}
                      style={{ background: 'none', border: '1px solid #B85042', color: '#B85042', borderRadius: 3, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontFamily: 'Arial, sans-serif', flexShrink: 0 }}>
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
